/**
 * Admin → Meme coins (dealer tools): house exposure per listed coin, the token list with listing and
 * state actions (list, pause, close-only, reopen, archive, settle) and the rules (listing gates, risk
 * tiers, caps, breaker). Every action needs a reason and is in the admin audit.
 */
import { useCallback, useEffect, useState } from 'react';
import { MEME_TOKEN_STATES, type MemeRiskTier, type MemeRules, type MemeToken, type MemeTokenState } from '@platform/shared';
import { cx, Modal, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { compactUsd, memePrice, stateText } from '../stores/meme';
import { RiskChip, StateBadge, TokenAvatar } from './meme/kit';

const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');
const input = 'h-9 w-full rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const btn = 'h-8 rounded-lg border border-line-strong px-3 text-[12px] font-semibold hover:bg-raised disabled:opacity-40';
const th = 'px-2.5 py-2 text-left text-[11px] font-semibold tracking-wide text-muted uppercase';
const td = 'num px-2.5 py-2';

export function MemeTab() {
  const [sub, setSub] = useState<'exposure' | 'tokens' | 'rules'>('exposure');
  return (
    <div className="flex flex-col gap-4">
      <div role="tablist" aria-label="Meme coins" className="flex w-fit gap-1 rounded-full border border-line p-[3px]">
        {(['exposure', 'tokens', 'rules'] as const).map((t) => (
          <button key={t} type="button" role="tab" aria-selected={sub === t} onClick={() => setSub(t)} className={cx('rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold capitalize', sub === t ? 'bg-raised' : 'text-muted')}>
            {t}
          </button>
        ))}
      </div>
      {sub === 'exposure' && <Exposure />}
      {sub === 'tokens' && <Tokens />}
      {sub === 'rules' && <Rules />}
    </div>
  );
}

// ---------- exposure ----------

interface ExposureRow {
  mint: string;
  symbol: string;
  tokenSymbol: string;
  imageUrl: string | null;
  state: MemeTokenState;
  positions: number;
  clients: number;
  netUsd: string | null;
  capUsd: string;
  utilisationPct: number | null;
  mark: string | null;
  spreadBps: number | null;
  frozen: boolean;
  healthySources: number | null;
  clientPnl: string | null;
}

function Exposure() {
  const [rows, setRows] = useState<ExposureRow[] | null>(null);
  const [e, setE] = useState<string | null>(null);
  const load = useCallback(() => {
    api
      .get<{ tokens: ExposureRow[] }>('/api/admin/meme/exposure')
      .then((r) => setRows(r.tokens))
      .catch((x) => setE(err(x)));
  }, []);
  useEffect(() => {
    load();
    const t = window.setInterval(load, 10_000);
    return () => window.clearInterval(t);
  }, [load]);
  if (e) return <p className="text-down">{e}</p>;
  if (!rows) return <Spinner />;
  if (!rows.length) return <p className="text-muted">No meme coin is listed.</p>;
  return (
    <div className="overflow-x-auto rounded-xl border border-line">
      <table className="w-full text-[12.5px]">
        <thead>
          <tr>
            {['Coin', 'Status', 'Price', 'Spread', 'Sources', 'Clients', 'House net', 'Cap', 'Used', 'Clients’ P/L'].map((h) => (
              <th key={h} className={th}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const st = stateText({ state: r.state, priceFrozen: r.frozen, haltedUntil: null });
            const house = r.netUsd === null ? null : Number(r.netUsd) === 0 ? 0 : -Number(r.netUsd);
            return (
              <tr key={r.mint} className="border-t border-line">
                <td className="px-2.5 py-2 font-semibold">
                  <span className="flex items-center gap-2">
                    <TokenAvatar token={{ symbol: r.tokenSymbol, mint: r.mint, imageUrl: r.imageUrl }} size={20} />
                    <span>
                      {r.tokenSymbol} <span className="font-normal text-muted">{r.symbol}</span>
                    </span>
                  </span>
                </td>
                <td className="px-2.5 py-2">
                  <StateBadge label={st.label} tone={st.tone} />
                </td>
                <td className={td}>{memePrice(r.mark)}</td>
                <td className={td}>{r.spreadBps === null ? '—' : `${(r.spreadBps / 100).toFixed(2)}%`}</td>
                <td className={cx(td, r.healthySources !== null && r.healthySources < 2 && 'text-down')}>{r.healthySources ?? '—'}</td>
                <td className={td}>
                  {r.clients} / {r.positions}
                </td>
                <td className={cx(td, house !== null && house < 0 ? 'text-down' : '')}>{house === null ? '—' : `${house.toLocaleString('en-US', { maximumFractionDigits: 0 })} USDT`}</td>
                <td className={td}>{Number(r.capUsd).toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                <td className={cx(td, (r.utilisationPct ?? 0) >= 80 && 'font-semibold text-down')}>{r.utilisationPct === null ? '—' : `${r.utilisationPct}%`}</td>
                <td className={td}>{r.clientPnl === null ? '—' : `${r.clientPnl} USDT`}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="m-0 border-t border-line px-3 py-2 text-[11.5px] text-muted">House net is the opposite of clients’ net position at our price (B-book). Over its cap a coin only takes orders that reduce our exposure.</p>
    </div>
  );
}

// ---------- tokens ----------

interface Detail {
  token: MemeToken;
  events: { kind: string; data: any; actor: string; at: string }[];
  sources: { source: string; price: string | null; at: string | null; healthy: boolean; deviationPct: number | null; why: string | null }[];
  gates: null | { failures: string[]; healthySources: number; mark: string | null; riskScore: number | null; maxLeverage: number | null };
}

type Action = { kind: 'state'; to: MemeTokenState } | { kind: 'settle' };

function Tokens() {
  const [state, setState] = useState<string>('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<MemeToken[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [mint, setMint] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (state) p.set('state', state);
    if (q.trim()) p.set('q', q.trim());
    api
      .get<{ tokens: MemeToken[] }>(`/api/admin/meme/tokens?${p}`)
      .then((r) => setRows(r.tokens))
      .catch((e) => setMsg({ ok: false, text: err(e) }));
  }, [state, q]);
  useEffect(() => {
    const t = window.setTimeout(load, 250);
    return () => window.clearTimeout(t);
  }, [load]);
  const add = async () => {
    setMsg(null);
    try {
      const r = await api.post<{ token: MemeToken }>('/api/admin/meme/tokens', { mint: mint.trim() });
      setMsg({ ok: true, text: `Added ${r.token.symbol} (watch only). Check its gates, then list it.` });
      setMint('');
      setOpen(r.token.mint);
      load();
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    }
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label="State" value={state} onChange={(e) => setState(e.target.value)} className="h-9 rounded-lg border border-line bg-bg px-2 text-[13px]">
          <option value="">Listed first</option>
          {MEME_TOKEN_STATES.map((s) => (
            <option key={s} value={s}>
              {s.replace('_', ' ')}
            </option>
          ))}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search symbol, name or mint" className={cx(input, 'max-w-[260px]')} />
        <span className="flex-1" />
        <input value={mint} onChange={(e) => setMint(e.target.value)} placeholder="Add a token by mint address" aria-label="Mint address" className={cx(input, 'max-w-[340px] font-mono text-[12px]')} />
        <button type="button" disabled={mint.trim().length < 32} onClick={() => void add()} className="h-9 rounded-lg bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50">
          Add
        </button>
      </div>
      {msg && <p className={cx('m-0 text-[12.5px]', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</p>}
      {!rows ? (
        <Spinner />
      ) : !rows.length ? (
        <p className="text-muted">No tokens.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr>
                {['Token', 'Status', 'Risk', 'Liquidity', '24h volume', 'Top 10', 'Dev', 'Instrument', 'Updated'].map((h) => (
                  <th key={h} className={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const st = stateText({ state: t.state, priceFrozen: false, haltedUntil: null });
                return [
                  <tr key={t.mint} onClick={() => setOpen(open === t.mint ? null : t.mint)} className={cx('cursor-pointer border-t border-line hover:bg-raised', open === t.mint && 'bg-raised')}>
                    <td className="px-2.5 py-2">
                      <span className="flex items-center gap-2">
                        <TokenAvatar token={t} size={24} />
                        <b>{t.symbol}</b>
                        <span className="text-muted">{t.name}</span>
                      </span>
                    </td>
                    <td className="px-2.5 py-2">
                      <StateBadge label={st.label} tone={st.tone} />
                    </td>
                    <td className="px-2.5 py-2">
                      <RiskChip score={t.riskScore} />
                    </td>
                    <td className={td}>{compactUsd(t.metrics.liquidityUsd)}</td>
                    <td className={td}>{compactUsd(t.metrics.volume24hUsd)}</td>
                    <td className={td}>{t.metrics.top10Pct ?? '—'}%</td>
                    <td className={td}>{t.metrics.devPct ?? '—'}%</td>
                    <td className={td}>{t.instrument ?? '—'}</td>
                    <td className="px-2.5 py-2 text-muted">{new Date(t.updatedAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</td>
                  </tr>,
                  open === t.mint && (
                    <tr key={`${t.mint}-d`}>
                      <td colSpan={9} className="border-t border-line bg-bg p-3">
                        <TokenDetail mint={t.mint} onChanged={load} />
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TokenDetail({ mint, onChanged }: { mint: string; onChanged: () => void }) {
  const [d, setD] = useState<Detail | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const load = useCallback(
    (gates = false) =>
      api
        .get<Detail>(`/api/admin/meme/tokens/${mint}${gates ? '?gates=1' : ''}`)
        .then(setD)
        .catch((e) => setMsg({ ok: false, text: err(e) })),
    [mint],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    setMsg(null);
    try {
      await fn();
      setMsg({ ok: true, text: ok });
      await load(key === 'gates');
      onChanged();
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    } finally {
      setBusy(null);
    }
  };
  if (!d) return <Spinner />;
  const s = d.token.state;
  return (
    <div className="flex flex-col gap-3 text-[12.5px]">
      <div className="flex flex-wrap gap-2">
        <button type="button" className={btn} disabled={!!busy} onClick={() => void run('gates', () => load(true), 'Gates checked against live data')}>
          {busy === 'gates' ? <Spinner /> : 'Check gates'}
        </button>
        <button type="button" className={btn} disabled={!!busy} onClick={() => void run('refresh', () => api.post(`/api/admin/meme/tokens/${mint}/refresh`), 'Facts refreshed')}>
          Refresh facts
        </button>
        {s === 'discovered' && (
          <button type="button" className={cx(btn, 'border-accent text-accent-fg')} disabled={!!busy} onClick={() => void run('list', () => api.post(`/api/admin/meme/tokens/${mint}/list`), 'Listed')}>
            List
          </button>
        )}
        {s === 'listed' && (
          <>
            <button type="button" className={btn} onClick={() => setAction({ kind: 'state', to: 'halted' })}>
              Pause…
            </button>
            <button type="button" className={cx(btn, 'text-down')} onClick={() => setAction({ kind: 'state', to: 'close_only' })}>
              Close only…
            </button>
          </>
        )}
        {(s === 'halted' || s === 'close_only') && (
          <button type="button" className={btn} onClick={() => setAction({ kind: 'state', to: 'listed' })}>
            Reopen…
          </button>
        )}
        {s === 'halted' && (
          <button type="button" className={cx(btn, 'text-down')} onClick={() => setAction({ kind: 'state', to: 'close_only' })}>
            Close only…
          </button>
        )}
        {s === 'close_only' && (
          <button type="button" className={cx(btn, 'border-down text-down')} onClick={() => setAction({ kind: 'settle' })}>
            Settle and delist…
          </button>
        )}
        {s === 'discovered' && (
          <button type="button" className={btn} onClick={() => setAction({ kind: 'state', to: 'archived' })}>
            Archive…
          </button>
        )}
      </div>
      {msg && <p className={cx('m-0', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</p>}
      {d.gates && (
        <div className={cx('rounded-lg px-3 py-2', d.gates.failures.length ? 'bg-warn-bg' : 'bg-up-bg')}>
          <b>{d.gates.failures.length ? 'Not listable yet' : 'Every gate passes'}</b> · {d.gates.healthySources} healthy price source(s) · mark {memePrice(d.gates.mark)} · max leverage {d.gates.maxLeverage ?? '—'}x
          {d.gates.failures.length > 0 && (
            <ul className="m-0 mt-1 pl-4">
              {d.gates.failures.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <div className="mb-1 font-semibold">Price sources</div>
          {d.sources.length ? (
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
              {d.sources.map((x) => (
                <li key={x.source} className="num flex justify-between gap-2">
                  <span>{x.source}</span>
                  <span className={x.healthy ? 'text-up' : 'text-down'}>
                    {memePrice(x.price)} {x.healthy ? '✓' : `✗ ${x.why ?? ''}`}
                    {x.deviationPct !== null ? ` · ${x.deviationPct}%` : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 text-muted">Only tracked while listed.</p>
          )}
        </div>
        <div>
          <div className="mb-1 font-semibold">History</div>
          <ul className="m-0 flex max-h-40 list-none flex-col gap-0.5 overflow-y-auto p-0 text-muted">
            {d.events.map((e, i) => (
              <li key={i}>
                {new Date(e.at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })} · {e.kind}
                {e.data?.to ? ` → ${e.data.to}` : ''}
                {e.data?.reason ? ` · ${e.data.reason}` : ''} · {e.actor}
              </li>
            ))}
            {!d.events.length && <li>Nothing yet.</li>}
          </ul>
        </div>
      </div>
      {action && (
        <ActionModal
          token={d.token}
          action={action}
          onClose={() => setAction(null)}
          onDone={(text) => {
            setAction(null);
            setMsg({ ok: true, text });
            void load();
            onChanged();
          }}
        />
      )}
    </div>
  );
}

function ActionModal({ token, action, onClose, onDone }: { token: MemeToken; action: Action; onClose: () => void; onDone: (text: string) => void }) {
  const [reason, setReason] = useState('');
  const [minutes, setMinutes] = useState('5');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);
  const [e, setE] = useState<string | null>(null);
  const settle = action.kind === 'settle';
  const title = settle ? `Settle and delist ${token.symbol}` : `${token.symbol}: ${action.to === 'halted' ? 'pause trading' : action.to === 'close_only' ? 'close only' : action.to === 'listed' ? 'reopen' : action.to}`;
  const go = async () => {
    setBusy(true);
    setE(null);
    try {
      if (settle) {
        const r = await api.post<{ price: string; method: string; positionsClosed: number; ordersCancelled: number }>(`/api/admin/meme/tokens/${token.mint}/settle`, { reason, ...(price ? { price } : {}) });
        onDone(`Settled at ${memePrice(r.price)} (${r.method === 'twap_1h' ? '1-hour average' : 'your price'}): ${r.positionsClosed} position(s) closed, ${r.ordersCancelled} order(s) cancelled`);
      } else {
        await api.post(`/api/admin/meme/tokens/${token.mint}/state`, { state: action.to, reason, ...(action.to === 'halted' ? { minutes: Number(minutes) } : {}) });
        onDone('Saved');
      }
    } catch (x) {
      setE(err(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={title} width={440}>
      {settle && (
        <p className="m-0 text-[12.5px] leading-relaxed text-muted">
          Cancels every pending order and closes every position at the average of our prices over the hour before the risk event, for buyers and sellers alike. Clients are told. This can't be undone.
        </p>
      )}
      {!settle && action.to === 'halted' && (
        <label className="flex flex-col gap-1 text-[12px] text-muted">
          Minutes
          <input value={minutes} inputMode="numeric" onChange={(x) => /^\d{0,4}$/.test(x.target.value) && setMinutes(x.target.value)} className={input} />
        </label>
      )}
      <label className="flex flex-col gap-1 text-[12px] text-muted">
        Reason (kept in the audit and sent to the CRM)
        <textarea value={reason} onChange={(x) => setReason(x.target.value.slice(0, 500))} className="min-h-[80px] rounded-lg border border-line bg-bg px-3 py-2 text-[13px] text-fg outline-none focus:border-line-strong" />
      </label>
      {settle && (
        <label className="flex flex-col gap-1 text-[12px] text-muted">
          Settlement price (only if there was no price in that hour)
          <input value={price} inputMode="decimal" onChange={(x) => /^\d*\.?\d*$/.test(x.target.value) && setPrice(x.target.value)} placeholder="Leave empty to use the 1-hour average" className={input} />
        </label>
      )}
      {e && <p className="m-0 text-[12px] text-down">{e}</p>}
      <div className="grid grid-cols-2 gap-2.5">
        <button type="button" onClick={onClose} className="h-10 rounded-lg border border-line-strong font-semibold">
          Cancel
        </button>
        <button type="button" disabled={!reason.trim() || busy} onClick={() => void go()} className={cx('h-10 rounded-lg font-semibold text-white disabled:opacity-50', settle || (!settle && action.to === 'close_only') ? 'bg-down' : 'bg-accent')}>
          {busy ? <Spinner /> : settle ? 'Settle' : 'Confirm'}
        </button>
      </div>
    </Modal>
  );
}

// ---------- rules ----------

const RULE_FIELDS: [string, string][] = [
  ['listing.minLiquidityUsd', 'Min pool liquidity (USDT)'],
  ['listing.minVolume24hUsd', 'Min 24h volume (USDT)'],
  ['listing.minAgeHours', 'Min age (hours)'],
  ['listing.maxTop10Pct', 'Max top 10 holders %'],
  ['listing.maxDevPct', 'Max developer holding %'],
  ['listing.maxListed', 'Max coins listed'],
  ['risk.houseCapUsd', 'House cap per coin (USDT)'],
  ['risk.houseCapLiquidityPct', 'House cap, % of pool liquidity'],
  ['risk.exposureAlertPct', 'Exposure alert at % of cap'],
  ['risk.breaker.move60sPct', 'Pause on a move over % in 60 s'],
  ['risk.breaker.move15mPct', 'Pause on a move over % in 15 min'],
  ['risk.breaker.haltMinutes', 'Pause length (minutes)'],
  ['risk.rug.liquidityDropPct', 'Close only when liquidity falls %'],
  ['risk.defaultMaxDeviationPct', 'Default max price move %'],
  ['risk.largeLossUsd', 'Report a loss over (USDT)'],
  ['pricing.maxSourceDeviationPct', 'Source outlier at % from median'],
  ['pricing.spread.baseBps', 'Base spread (bps)'],
  ['pricing.spread.maxBps', 'Max spread (bps)'],
];
const get = (o: any, p: string) => p.split('.').reduce((x, k) => x?.[k], o);
function set(o: any, p: string, v: unknown) {
  const ks = p.split('.');
  let x = o;
  for (const k of ks.slice(0, -1)) x = x[k] ??= {};
  x[ks.at(-1)!] = v;
}

function Rules() {
  const [rules, setRules] = useState<MemeRules | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [auto, setAuto] = useState(false);
  const [tiers, setTiers] = useState<MemeRiskTier[]>([]);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const fill = (r: MemeRules) => {
    setRules(r);
    setDraft(Object.fromEntries(RULE_FIELDS.map(([p]) => [p, String(get(r, p))])));
    setAuto(r.listing.autoApprove);
    setTiers(r.risk.tiers);
  };
  useEffect(() => {
    api
      .get<{ rules: MemeRules }>('/api/admin/meme/rules')
      .then((r) => fill(r.rules))
      .catch((e) => setMsg({ ok: false, text: err(e) }));
  }, []);
  if (!rules) return msg ? <p className="text-down">{msg.text}</p> : <Spinner />;
  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const body: any = {};
      for (const [p] of RULE_FIELDS) set(body, p, Number(draft[p]));
      set(body, 'listing.autoApprove', auto);
      set(body, 'risk.tiers', tiers);
      const r = await api.put<{ rules: MemeRules }>('/api/admin/meme/rules', { rules: body });
      fill(r.rules);
      setMsg({ ok: true, text: 'Saved. New listings use these values; a listed coin keeps its leverage and cap until it is re-listed.' });
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex max-w-[860px] flex-col gap-4">
      <label className="flex items-center gap-2.5 rounded-lg border border-warn/30 bg-warn-bg p-3 text-[13px]">
        <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} className="size-4" />
        <span>
          <b>List automatically</b> when a graduated coin passes every gate (otherwise a dealer lists it here).
        </span>
      </label>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {RULE_FIELDS.map(([p, label]) => (
          <label key={p} className="flex flex-col gap-1 text-[12px] text-muted">
            {label}
            <input value={draft[p] ?? ''} inputMode="decimal" onChange={(e) => /^\d*\.?\d*$/.test(e.target.value) && setDraft({ ...draft, [p]: e.target.value })} className={cx(input, 'num')} />
          </label>
        ))}
      </div>
      <div>
        <div className="mb-1.5 text-[13px] font-semibold">Leverage tiers by risk score</div>
        <table className="text-[12.5px]">
          <thead>
            <tr>
              <th className={th}>Risk up to</th>
              <th className={th}>Max leverage</th>
              <th className={th}>Max per client (USDT)</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((t, i) => (
              <tr key={i}>
                {(['maxRisk', 'leverage', 'clientCapUsd'] as const).map((k) => (
                  <td key={k} className="px-1 py-1">
                    <input
                      value={String(t[k])}
                      inputMode="numeric"
                      aria-label={k}
                      onChange={(e) => /^\d*$/.test(e.target.value) && setTiers(tiers.map((x, j) => (j === i ? { ...x, [k]: Number(e.target.value) } : x)))}
                      className={cx(input, 'num w-[120px]')}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="m-0 mt-1 text-[11.5px] text-muted">A coin whose risk score is above the last tier is never listed.</p>
      </div>
      {msg && <p className={cx('m-0 text-[12.5px]', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</p>}
      <button type="button" disabled={saving} onClick={() => void save()} className="h-9 w-fit rounded-lg bg-accent px-5 text-xs font-semibold text-white disabled:opacity-50">
        {saving ? <Spinner /> : 'Save rules'}
      </button>
    </div>
  );
}
